import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionDataBo } from "@/types/tableAction";
import { zodResolver } from "@hookform/resolvers/zod";
import { NotificationModal } from "@/components/shared/modal/notification.modal";
import { NotesModal } from "@/components/shared/modal/notes.modal";
import { Button } from "@/components/ui/button";
import {
  CircleCheck,
  CircleX,
  FileText,
  Loader2,
  MessageCircleMore,
  Save,
  NotebookPen,
  Bell,
  X,
  Trash,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { InformationDisplay, ProtectedInformation } from "./informationDisplay";
import { DocumentListItem } from "@/components/shared/documentListItem";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { ConfirmationModal } from "@/components/shared/confirmation.modal";
import { TypeEnvoi } from "@/components/shared/badge/typeEnvoi.badge";
import { InfoClientCustomQuotationBo } from "./infoClientBo";
import { InfoPickupCustomQuotationBo, InfoValidationCustomQuotationBo } from "./infoValidationBo";
import {
  ReCustomQuotationFormData,
  ReCustomQuotationSchema,
} from "@/components/backOffice/pendingCustomQuotation/customQuotationQuote.form";
import { getReCustomQuotationDefault } from "@/components/backOffice/pendingCustomQuotation/modal/customQuotationQuoteDefault";
import { createReCustomQuotationPayload } from "./customQuotation.payload";
import { CustomQuotationOtherTypeBadge } from "@/components/shared/badge/customQuotationOtherType.badge";
import { OtherCustomQuotationTypeEnum } from "@/constants/enums/otherCustomQuotationType.enum";
import { CustomQuotationTypeEnum } from "@/constants/enums/customQuotationType.enum";
import { ConfirmationModalProps } from "@/types/confirmationModal";
import {
  MainModalProps,
  ModalContentProps,
  SideModalProps,
} from "@/types/bo/updateExpedition.type";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { volumesOptions } from "@/constants/options/volumes.option";
import { ControlledCustomTextarea } from "@/components/ui/customs/controlled-custom-textarea";
import { InfoReExpeBo } from "./infoReExpeBo";
import { useCreateReCustomQuotationBo } from "@/api/expedition.api";
import { CustomQuotationTypeBadge } from "@/components/shared/badge/customQuotationType.badge";
import { sortDocumentsByPriority } from "@/constants/fileCrud";
import { getExpeditionIdentifier } from "@/lib/expedition.util";
import {
  useOtherCustomQuotationTypeOptions,
  useParcelProductTypeOptions,
} from "@/hooks/useEnumOptions";

export const ReCustomQuotationModalContent = ({
  expedition,
  company,
  onClose,
  customs,
}: ModalContentProps) => {
  const [confirmationModalState, setConfirmationModalState] =
    useState<ConfirmationModalProps | null>(null);
  const [actionData, setActionData] = useState<ActionDataBo | null>(null);

  const { mutateAsync: createReCustomQuotationBo, isPending } = useCreateReCustomQuotationBo();

  const method = useForm<ReCustomQuotationFormData>({
    resolver: zodResolver(ReCustomQuotationSchema),
    defaultValues: getReCustomQuotationDefault(expedition),
  });

  const onSubmit = async (data: ReCustomQuotationFormData) => {
    const payload = createReCustomQuotationPayload({
      expedition,
      data,
    });

    const ret = await createReCustomQuotationBo(payload);
    if (ret.status === 200 || ret.status === 201) {
      onClose();
    }
  };

  return (
    <FormProvider {...method}>
      <form
        onSubmit={method.handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
      >
        <div className="flex h-[80vh] w-full flex-col-reverse justify-between gap-2 overflow-y-hidden lg:flex-row">
          <CustomQuotationMainModal
            expedition={expedition}
            company={company}
            customs={customs}
            isPending={isPending}
            setConfirmationModalState={setConfirmationModalState}
          />
          <CustomQuotationEditableSideModal
            expedition={expedition}
            company={company}
            onClose={onClose}
            setConfirmationModalState={setConfirmationModalState}
            handleSave={() => {}}
            isPending={isPending}
            setActionData={setActionData}
          />
        </div>
      </form>
      {confirmationModalState && (
        <ConfirmationModal
          open={!!confirmationModalState}
          onClose={() => setConfirmationModalState(null)}
          title={confirmationModalState.title}
          text={confirmationModalState.text}
          confirm={confirmationModalState.confirm}
        />
      )}
      {actionData && actionData.action === "NOTIFICATION" && (
        <NotificationModal
          expeditionId={actionData.expedition.id}
          open={true}
          onClose={() => setActionData(null)}
        />
      )}
      {actionData && actionData.action === "NOTES" && (
        <NotesModal
          expedition={actionData.expedition}
          open={true}
          onClose={() => setActionData(null)}
        />
      )}
    </FormProvider>
  );
};

const CustomQuotationMainModal = ({
  expedition,
  company,
  isPending,
  setConfirmationModalState,
  customs,
}: MainModalProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const customQuotationType = expedition.customQuotation?.type as CustomQuotationTypeEnum;

  if (!customQuotationType) return <p>{t("reCustomQuotation.undefinedType")}</p>;

  return (
    <div className="flex flex-1/2 flex-col gap-1 overflow-y-auto">
      <InfoClientCustomQuotationBo customQuotationType={customQuotationType} customs={customs} />
      <InfoPickupCustomQuotationBo
        company={company}
        setConfirmationModalState={setConfirmationModalState}
      />
      <InfoValidationCustomQuotationBo
        company={company}
        setConfirmationModalState={setConfirmationModalState}
      />
      <InfoContent customQuotationType={customQuotationType} />
      <InfoQuote />
      <InfoReExpeBo index={6} />
      <Button
        variant={"outline"}
        type="submit"
        className="mt-2 w-[200px] self-end"
        disabled={isPending}
      >
        {isPending ? <Loader2 className="animate-spin" /> : t("reCustomQuotation.submit")}
      </Button>
    </div>
  );
};

const CustomQuotationEditableSideModal = ({
  expedition,
  company,
  onClose,
  setConfirmationModalState,
  setActionData,
  handleSave,
}: SideModalProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const method = useFormContext<ReCustomQuotationFormData>();

  const expeditionIdentifier = getExpeditionIdentifier(expedition);
  const hasUnreadNotif =
    (expedition.notification?.length ?? 0) > 0 &&
    expedition.notification?.some((n) => n.readBy == null);

  const pickupLocationId = expedition.pickupInfo?.pickupLocation?.id || "";
  const billingEntityId = expedition.expeditionBillingDetail?.billingEntityId;

  const expressWatch = method.watch("express");
  const insuranceWatch = method.watch("insurance");

  const isMultipleExpedition =
    expedition.customQuotation?.otherDetail?.isMultipleExpedition || false;

  const pickupLocation = company.pickUpLocations.find(
    (pickUpLocation) => pickUpLocation.id === pickupLocationId,
  );

  const billingEntity = company.billingEntities.find(
    (billingEntity) => billingEntity.id === billingEntityId,
  );

  const sortedDocuments = useMemo(
    () => sortDocumentsByPriority(expedition.expeditionDocuments?.documents ?? []),
    [expedition.expeditionDocuments?.documents],
  );

  const pickupAddress = pickupLocation
    ? pickupLocation?.street + ", " + pickupLocation?.postalCode + " " + pickupLocation?.city
    : expedition.pickupInfo?.pickupStreet +
      ", " +
      expedition.pickupInfo?.pickupPostalCode +
      " " +
      expedition.pickupInfo?.pickupCity +
      ", " +
      expedition.pickupInfo?.pickupCountry;

  const customQuotationType = expedition.customQuotation?.type as CustomQuotationTypeEnum;

  if (!customQuotationType) return <p>{t("reCustomQuotation.undefinedType")}</p>;

  return (
    <div className="relative flex h-full flex-1/6 flex-col gap-0 overflow-y-auto rounded-lg bg-white p-0">
      <div className="sticky top-0 flex flex-col gap-0 border-b bg-white p-4">
        <div className="flex flex-row items-center justify-between gap-4">
          <h3 className="text-lg font-semibold text-black">{expeditionIdentifier}</h3>
          <div className="flex flex-row items-center justify-between gap-0">
            <Button
              variant="ghost"
              onClick={() => setActionData({ expedition, action: "NOTES" })}
              className="relative h-12 w-12 [&_svg]:h-8 [&_svg]:w-8"
              type="button"
            >
              <NotebookPen className="h-4 w-4" />
              <Badge variant="destructive" className="absolute -top-0 -right-0 h-4 w-4">
                {expedition.expeditionNotes?.length}
              </Badge>
            </Button>
            <Button
              variant="ghost"
              onClick={() => setActionData({ expedition, action: "NOTIFICATION" })}
              className={`relative h-12 w-12 [&_svg]:h-8 [&_svg]:w-8 ${hasUnreadNotif ? "bg-yellow-400 text-white hover:bg-yellow-500" : ""}`}
              type="button"
            >
              <Bell className="h-4 w-4" />
              <Badge variant="destructive" className="absolute -top-0 -right-0 h-4 w-4">
                {expedition.notification?.length}
              </Badge>
            </Button>
            <Button
              variant="ghost"
              onClick={() => handleSave()}
              type="button"
              className={`h-12 w-12 [&_svg]:h-8 [&_svg]:w-8 ${expedition.processStatus === "VALIDATED" && "hidden"}`}
            >
              <Save className="text-brand600 h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              onClick={onClose}
              type="button"
              className="h-12 w-12 text-red-600 [&_svg]:h-8 [&_svg]:w-8"
            >
              <X className="h-4 w-4 text-red-600" />
            </Button>
          </div>
        </div>
      </div>
      <div className="flex h-full flex-1/6 flex-col gap-2 overflow-y-auto rounded-lg bg-white px-4 pt-2">
        <div id="commentContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
          <h4 className="min-w-fit text-base font-bold text-gray-900">
            {t("reCustomQuotation.optionsTitle")}
          </h4>
          <ProtectedInformation
            label={t("reCustomQuotation.shippingTypeLabel")}
            value={<TypeEnvoi express={expressWatch} />}
            onClick={() => {
              setConfirmationModalState({
                title: t("reCustomQuotation.shippingTypeConfirmTitle"),
                text: expressWatch
                  ? t("reCustomQuotation.shippingTypeToStandard")
                  : t("reCustomQuotation.shippingTypeToExpress"),
                confirm: () => {
                  method.setValue("express", !expressWatch);
                },
              });
            }}
          />
          <ProtectedInformation
            label={t("reCustomQuotation.insuranceLabel")}
            value={
              insuranceWatch ? (
                <CircleCheck className="h-6 w-6 fill-green-500 text-white" />
              ) : (
                <CircleX className="h-6 w-6 fill-red-500 text-white" />
              )
            }
            onClick={() =>
              setConfirmationModalState({
                title: t("reCustomQuotation.insuranceConfirmTitle"),
                text: insuranceWatch
                  ? t("reCustomQuotation.insuranceDisableText")
                  : t("reCustomQuotation.insuranceEnableText"),
                confirm: () => {
                  method.setValue("insurance", !insuranceWatch);
                },
              })
            }
          />
        </div>
        {customQuotationType === "OTHER" && (
          <div id="commentContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="min-w-fit text-base font-bold text-gray-900">
                {t("reCustomQuotation.additionalInfoTitle")}
              </h4>
              <CustomQuotationOtherTypeBadge
                type={
                  expedition.customQuotation?.otherDetail
                    ?.otherCustomQuotationType as OtherCustomQuotationTypeEnum
                }
              />
            </div>
            <ProtectedInformation
              label={t("reCustomQuotation.expeditionCountLabel")}
              value={(expedition.customQuotation?.otherDetail?.numberOfExpedition || 1).toString()}
            />
            {!isMultipleExpedition ? (
              <>
                <InformationDisplay
                  label={t("reCustomQuotation.recipientLabel")}
                  value={
                    expedition.deliveryInfo?.firstName + " " + expedition.deliveryInfo?.lastName
                  }
                />
                <InformationDisplay
                  label={t("reCustomQuotation.recipientCompanyLabel")}
                  value={expedition.deliveryInfo?.company || "-"}
                />
                <InformationDisplay
                  label={t("reCustomQuotation.addressLabel")}
                  value={
                    expedition.deliveryInfo?.street +
                    ", " +
                    expedition.deliveryInfo?.postalCode +
                    " " +
                    expedition.deliveryInfo?.city +
                    ", " +
                    expedition.deliveryInfo?.country
                  }
                />
                <InformationDisplay
                  label={t("reCustomQuotation.phoneLabel")}
                  value={expedition.deliveryInfo?.phone || "-"}
                />
                <InformationDisplay
                  label={t("reCustomQuotation.emailLabel")}
                  value={expedition.deliveryInfo?.email || "-"}
                />
              </>
            ) : (
              <InformationDisplay
                label={t("reCustomQuotation.recipientLabel")}
                value={t("reCustomQuotation.unavailableForMultiple")}
              />
            )}
          </div>
        )}
        {customQuotationType === "PALETTE" && (
          <>
            <div id="commentContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="min-w-fit text-base font-bold text-gray-900">
                  {t("reCustomQuotation.deliveryInfoTitle")}
                </h4>
              </div>
              <div className="flex flex-col gap-0">
                <InformationDisplay
                  label={t("reCustomQuotation.recipientLabel")}
                  value={
                    expedition.deliveryInfo?.firstName + " " + expedition.deliveryInfo?.lastName
                  }
                />
                <InformationDisplay
                  label={t("reCustomQuotation.recipientCompanyLabel")}
                  value={expedition.deliveryInfo?.company || "-"}
                />
                <InformationDisplay
                  label={t("reCustomQuotation.addressLabel")}
                  value={
                    expedition.deliveryInfo?.street +
                    ", " +
                    expedition.deliveryInfo?.postalCode +
                    " " +
                    expedition.deliveryInfo?.city +
                    ", " +
                    expedition.deliveryInfo?.country
                  }
                />
                <InformationDisplay
                  label={t("reCustomQuotation.phoneLabel")}
                  value={expedition.deliveryInfo?.phone || "-"}
                />
                <InformationDisplay
                  label={t("reCustomQuotation.emailLabel")}
                  value={expedition.deliveryInfo?.email || "-"}
                />
              </div>
            </div>
            <div id="commentContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="min-w-fit text-base font-bold text-gray-900">
                  {t("reCustomQuotation.productInfoTitle")}
                </h4>
                <CustomQuotationTypeBadge type={customQuotationType as CustomQuotationTypeEnum} />
              </div>
            </div>
          </>
        )}
        {customQuotationType === "PARCEL" && (
          <>
            <div id="commentContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="min-w-fit text-base font-bold text-gray-900">
                  {t("reCustomQuotation.deliveryInfoTitle")}
                </h4>
              </div>
              <div className="flex flex-col gap-0">
                <InformationDisplay
                  label={t("reCustomQuotation.recipientLabel")}
                  value={
                    expedition.deliveryInfo?.firstName + " " + expedition.deliveryInfo?.lastName
                  }
                />
                <InformationDisplay
                  label={t("reCustomQuotation.recipientCompanyLabel")}
                  value={expedition.deliveryInfo?.company || "-"}
                />
                <InformationDisplay
                  label={t("reCustomQuotation.addressLabel")}
                  value={
                    expedition.deliveryInfo?.street +
                    ", " +
                    expedition.deliveryInfo?.postalCode +
                    " " +
                    expedition.deliveryInfo?.city +
                    ", " +
                    expedition.deliveryInfo?.country
                  }
                />
                <InformationDisplay
                  label={t("reCustomQuotation.phoneLabel")}
                  value={expedition.deliveryInfo?.phone || "-"}
                />
                <InformationDisplay
                  label={t("reCustomQuotation.emailLabel")}
                  value={expedition.deliveryInfo?.email || "-"}
                />
              </div>
            </div>
            <div id="commentContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="min-w-fit text-base font-bold text-gray-900">
                  {t("reCustomQuotation.productInfoTitle")}
                </h4>
                <CustomQuotationTypeBadge type={customQuotationType as CustomQuotationTypeEnum} />
              </div>
            </div>
          </>
        )}
        <div id="commentContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
          <div className="flex flex-row items-center gap-2">
            <h4 className="text-base font-bold text-gray-900">
              {t("reCustomQuotation.clientCommentTitle")}
            </h4>
            <MessageCircleMore className="fill-black text-gray-100" />
          </div>
          <p className="text-base text-gray-600">{expedition.deliveryInfo?.comment || "-"}</p>
        </div>
        <div id="fileContainer" className="flex flex-col gap-1 rounded-md bg-gray-100 p-2">
          <div className="flex flex-row items-center gap-2">
            <h4 className="text-base font-bold text-gray-900">
              {t("reCustomQuotation.documentsTitle")}
            </h4>
            <FileText className="fill-black text-gray-100" />
          </div>
          {sortedDocuments.length > 0 ? (
            <div className="flex flex-col">
              {sortedDocuments.map((doc) => (
                <DocumentListItem key={doc.id} file={doc} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">{t("reCustomQuotation.noDocuments")}</p>
          )}
        </div>
        <div id="userContainer" className="flex flex-col gap-0 p-2">
          <InformationDisplay
            label={t("reCustomQuotation.userLabel")}
            value={expedition.createdBy?.firstName + " " + expedition.createdBy?.lastName}
            bold
          />
          <InformationDisplay
            label={t("reCustomQuotation.phoneLabel")}
            value={expedition.createdBy?.mobilePhoneNumber || "-"}
          />
          <InformationDisplay
            label={t("reCustomQuotation.emailLabel")}
            value={expedition.createdBy?.email || "-"}
          />
        </div>
        <div id="billingContainer" className="flex flex-col gap-0 p-2">
          <InformationDisplay
            label={t("reCustomQuotation.billingLabel")}
            value={billingEntity?.companyName || "-"}
            bold
          />
          <InformationDisplay
            label={t("reCustomQuotation.emailLabel")}
            value={billingEntity?.email || "-"}
          />
        </div>
        {pickupLocation ? (
          <div id="pickupContainer" className="flex flex-col gap-0 p-2">
            <InformationDisplay
              label={t("reCustomQuotation.pickupLabel")}
              value={pickupLocation?.name || "-"}
              bold
            />
            <InformationDisplay
              label={t("reCustomQuotation.addressLabel")}
              value={pickupAddress || "-"}
            />
            <InformationDisplay
              label={t("reCustomQuotation.phoneLabel")}
              value={pickupLocation?.contactNumber || "-"}
            />
          </div>
        ) : (
          <div id="pickupContainer" className="flex flex-col gap-0 p-2">
            <InformationDisplay
              label={t("reCustomQuotation.pickupLabel")}
              value={
                expedition.pickupInfo?.pickupCompany ||
                expedition.pickupInfo?.pickupFirstName +
                  " " +
                  expedition.pickupInfo?.pickupLastName ||
                "-"
              }
              bold
            />
            <InformationDisplay
              label={t("reCustomQuotation.addressLabel")}
              value={pickupAddress || "-"}
            />
            <InformationDisplay
              label={t("reCustomQuotation.phoneLabel")}
              value={expedition.pickupInfo?.pickupPhone || "-"}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const InfoQuote = () => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<ReCustomQuotationFormData>();

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-blue-50 p-4">
      <div className="flex w-full flex-row items-center gap-2">
        <div className="bg-brand600 rounded-full px-3 py-1 text-xl font-bold text-white">
          <p className="text-xl font-bold text-white">5</p>
        </div>
        <p className="text-xl font-bold">{t("reCustomQuotationQuote.title")}</p>
      </div>
      <div className="flex w-full flex-col gap-1">
        <h3 className="text-sm font-bold">{t("reCustomQuotationQuote.deliveryDataTitle")}</h3>
        <div className="grid w-full grid-cols-2 gap-2">
          <ControlledUnifiedInput
            control={methods.control}
            name={`invoiceData.client`}
            label={t("reCustomQuotationQuote.clientLabel")}
            inputType="text"
            placeholder={t("reCustomQuotationQuote.clientPlaceholder")}
          />
          {/* <ControlledCustomsSelect
            customs={customs}
            name={`invoiceData.customId`}
            control={methods.control}
            placeholder="Pays de destination"
          /> */}
          <ControlledUnifiedInput
            control={methods.control}
            name={`invoiceData.reference`}
            label={t("reCustomQuotationQuote.referenceLabel")}
            inputType="text"
            placeholder={t("reCustomQuotationQuote.referencePlaceholder")}
          />
        </div>
        <h3 className="text-sm font-bold">{t("reCustomQuotationQuote.billingDataTitle")}</h3>
        <div className="grid w-full grid-cols-2 gap-2">
          <ControlledUnifiedInput
            control={methods.control}
            name={`invoiceData.transportHt`}
            label={t("reCustomQuotationQuote.transportLabel")}
            rightElement={<p>€</p>}
            inputType="float"
            placeholder={t("reCustomQuotationQuote.transportPlaceholder")}
          />
          <ControlledUnifiedInput
            control={methods.control}
            name={`invoiceData.packaging`}
            label={t("reCustomQuotationQuote.packagingLabel")}
            rightElement={<p>€</p>}
            inputType="float"
            placeholder={t("reCustomQuotationQuote.packagingPlaceholder")}
          />
          <ControlledUnifiedInput
            control={methods.control}
            name={`invoiceData.packagingProtection`}
            label={t("reCustomQuotationQuote.packagingProtectionLabel")}
            rightElement={<p>€</p>}
            inputType="float"
            placeholder={t("reCustomQuotationQuote.packagingProtectionPlaceholder")}
          />
          <ControlledUnifiedInput
            control={methods.control}
            name={`invoiceData.taxes`}
            label={t("reCustomQuotationQuote.taxesLabel")}
            rightElement={<p>€</p>}
            inputType="float"
            placeholder={t("reCustomQuotationQuote.taxesPlaceholder")}
          />
          <ControlledUnifiedInput
            control={methods.control}
            name={`invoiceData.insurance`}
            label={t("reCustomQuotationQuote.insuranceLabel")}
            rightElement={<p>€</p>}
            inputType="float"
            placeholder={t("reCustomQuotationQuote.insurancePlaceholder")}
          />
          <ControlledUnifiedInput
            control={methods.control}
            name={`invoiceData.tva`}
            label={t("reCustomQuotationQuote.vatLabel")}
            rightElement={<p>€</p>}
            inputType="float"
            placeholder={t("reCustomQuotationQuote.vatPlaceholder")}
          />
        </div>
        <div className="flex flex-row items-center gap-2">
          <p className="text-xl font-bold">{t("reCustomQuotationQuote.total")}</p>
          <p className="text-xl font-bold">
            {Number(
              (methods.watch(`invoiceData.transportHt`) || 0) +
                (methods.watch(`invoiceData.packaging`) || 0) +
                (methods.watch(`invoiceData.packagingProtection`) || 0) +
                (methods.watch(`invoiceData.taxes`) || 0) +
                (methods.watch(`invoiceData.insurance`) || 0) +
                (methods.watch(`invoiceData.tva`) || 0),
            ).toFixed(2)}
            €
          </p>
        </div>
      </div>
    </div>
  );
};

const InfoContent = ({ customQuotationType }: { customQuotationType: CustomQuotationTypeEnum }) => {
  const { t } = useTranslation("boExpeditionForm");
  const otherCustomQuotationOptions = useOtherCustomQuotationTypeOptions();
  const parcelProductTypeOptions = useParcelProductTypeOptions();
  const methods = useFormContext<ReCustomQuotationFormData>();

  const paletteProducts = methods.watch("paletteProducts");
  const palettePackages = methods.watch("palettePackages");
  const parcelProducts = methods.watch("parcelProducts");
  const parcelPackages = methods.watch("parcelPackages");

  const removePaletteProduct = (index: number) => {
    methods.setValue(
      "paletteProducts",
      paletteProducts?.filter((_, i) => i !== index),
    );
  };
  const removePalettePackage = (index: number) => {
    methods.setValue(
      "palettePackages",
      palettePackages?.filter((_, i) => i !== index),
    );
  };
  const removeParcelProduct = (index: number) => {
    methods.setValue(
      "parcelProducts",
      parcelProducts?.filter((_, i) => i !== index),
    );
  };
  const removeParcelPackage = (index: number) => {
    methods.setValue(
      "parcelPackages",
      parcelPackages?.filter((_, i) => i !== index),
    );
  };

  const addPaletteProduct = () => {
    methods.setValue("paletteProducts", [
      ...(paletteProducts || []),
      {
        volume: undefined as unknown as number,
        bottleQuantity: undefined as unknown as number,
      },
    ]);
  };
  const addPalettePackage = () => {
    methods.setValue("palettePackages", [
      ...(palettePackages || []),
      { value: 0, length: 0, width: 0, height: 0, weight: 0 },
    ]);
  };
  const addParcelProduct = () => {
    methods.setValue("parcelProducts", [
      ...(parcelProducts || []),
      {
        type: "BOTTLE",
        volume: undefined as unknown as number,
        bottleQuantity: undefined as unknown as number,
        description: "",
      },
    ]);
  };
  const addParcelPackage = () => {
    methods.setValue("parcelPackages", [
      ...(parcelPackages || []),
      { length: 0, width: 0, height: 0, weight: 0, value: 0 },
    ]);
  };

  if (customQuotationType === "OTHER") {
    return (
      <div className="flex flex-col gap-2 rounded-lg bg-blue-50 p-4">
        <div className="flex w-full flex-row items-center gap-2">
          <div className="bg-brand600 rounded-full px-3 py-1 text-xl font-bold text-white">
            <p className="text-xl font-bold text-white">4</p>
          </div>
          <p className="text-xl font-bold">{t("reCustomQuotationContent.otherTitle")}</p>
        </div>
        <div className="flex w-full flex-col gap-1">
          <ControlledSelectV2
            name="otherCustomQuotationType"
            control={methods.control}
            options={otherCustomQuotationOptions}
            placeholder={t("reCustomQuotationContent.otherTypePlaceholder")}
            label={t("reCustomQuotationContent.otherTypeLabel")}
          />
        </div>
      </div>
    );
  }

  if (customQuotationType === "PALETTE") {
    return (
      <div className="flex flex-col gap-2 rounded-lg bg-blue-50 p-4">
        <div className="flex w-full flex-row items-center gap-2">
          <div className="bg-brand600 rounded-full px-3 py-1 text-xl font-bold text-white">
            <p className="text-xl font-bold text-white">4</p>
          </div>
          <p className="text-xl font-bold">{t("reCustomQuotationContent.paletteTitle")}</p>
        </div>
        <div className="flex w-full flex-col gap-1">
          <h2 className="text-brand700 text-xl font-semibold">
            {t("reCustomQuotationContent.paletteProductsTitle")}
          </h2>
          <div className="flex w-full flex-col items-end gap-2">
            {paletteProducts?.map((_field, index) => (
              <div
                className="flex w-full flex-col items-start gap-2"
                key={`paletteProduct-${index}`}
              >
                <p className="font-semibold text-gray-700">
                  {t("reCustomQuotationContent.productTitle", { number: index + 1 })}
                </p>
                <div className="flex w-full flex-1 flex-row items-start sm:flex-row sm:gap-2">
                  <ControlledSelectV2
                    name={`paletteProducts.${index}.volume`}
                    control={methods.control}
                    options={volumesOptions.map((volume) => ({
                      label: volume.label,
                      value: volume.value.toString(),
                    }))}
                    placeholder={t("reCustomQuotationContent.volumePlaceholder")}
                    label={t("reCustomQuotationContent.volumeLabel")}
                    wrapperClassName="w-full"
                  />
                  <div className="flex w-full flex-row items-start gap-2">
                    <ControlledUnifiedInput
                      label={t("reCustomQuotationContent.bottleQuantityLabel")}
                      control={methods.control}
                      name={`paletteProducts.${index}.bottleQuantity`}
                      className="w-full"
                      inputType="integer"
                      wrapperClassName="w-full"
                    />
                    {paletteProducts?.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePaletteProduct(index)}
                        className="mt-[15px] flex items-start justify-center hover:bg-gray-100 focus:outline-hidden"
                      >
                        <Trash className="cursor-pointer text-red-600" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addPaletteProduct()}
              className="bg-brand50 text-brand700 hover:border-brand600 h-fit w-fit rounded-lg px-4 py-3 text-base font-semibold focus:outline-hidden"
            >
              {t("reCustomQuotationContent.addProduct")}
            </button>
          </div>
          <h2 className="text-brand700 text-xl font-semibold">
            {t("reCustomQuotationContent.paletteDimensionsTitle")}
          </h2>
          <div className="flex w-full flex-col items-end gap-2">
            {palettePackages?.map((_field, index) => (
              <div className="flex flex-col items-start gap-2" key={`palettePackage-${index}`}>
                <p className="font-semibold text-gray-700">
                  {t("reCustomQuotationContent.paletteTitleNumbered", { number: index + 1 })}
                </p>
                <div className="flex flex-col items-start gap-2 md:flex-row">
                  <div className="flex w-full flex-row items-start gap-2">
                    <ControlledUnifiedInput
                      label={t("reCustomQuotationContent.lengthLabel")}
                      control={methods.control}
                      name={`palettePackages.${index}.length`}
                      className="w-full"
                      rightElement={<p className="text-xs text-gray-600">cm</p>}
                      inputType="integer"
                      wrapperClassName="w-full"
                    />
                    <ControlledUnifiedInput
                      label={t("reCustomQuotationContent.widthLabel")}
                      control={methods.control}
                      name={`palettePackages.${index}.width`}
                      className="w-full"
                      rightElement={<p className="text-xs text-gray-600">cm</p>}
                      inputType="integer"
                      wrapperClassName="w-full"
                    />
                  </div>
                  <div className="flex w-full flex-row items-start gap-2">
                    <ControlledUnifiedInput
                      label={t("reCustomQuotationContent.heightLabel")}
                      control={methods.control}
                      name={`palettePackages.${index}.height`}
                      className="w-full"
                      rightElement={<p className="text-xs text-gray-600">cm</p>}
                      inputType="integer"
                      wrapperClassName="w-full"
                    />
                    <ControlledUnifiedInput
                      label={t("reCustomQuotationContent.weightLabel")}
                      control={methods.control}
                      name={`palettePackages.${index}.weight`}
                      className="w-full"
                      tooltip={t("reCustomQuotationContent.paletteWeightTooltip")}
                      rightElement={<p className="text-xs text-gray-600">kg</p>}
                      inputType="integer"
                      wrapperClassName="w-full"
                    />
                  </div>
                </div>
                <div className="flex w-full flex-row items-start gap-2">
                  <ControlledUnifiedInput
                    label={t("reCustomQuotationContent.valueLabel")}
                    control={methods.control}
                    name={`palettePackages.${index}.value`}
                    className="w-full"
                    rightElement={<p>€</p>}
                    tooltip={t("reCustomQuotationContent.paletteValueTooltip")}
                    inputType="integer"
                    wrapperClassName="w-full"
                  />
                  {palettePackages?.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePalettePackage(index)}
                      className="mt-[15px] flex items-start justify-center hover:bg-gray-100 focus:outline-hidden"
                    >
                      <Trash className="cursor-pointer text-red-600" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addPalettePackage()}
              className="bg-brand50 text-brand700 hover:border-brand600 h-fit w-fit rounded-lg px-4 py-3 text-base font-semibold focus:outline-hidden"
            >
              {t("reCustomQuotationContent.addPalette")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (customQuotationType === "PARCEL") {
    return (
      <div className="flex flex-col gap-2 rounded-lg bg-blue-50 p-4">
        <div className="flex w-full flex-row items-center gap-2">
          <div className="bg-brand600 rounded-full px-3 py-1 text-xl font-bold text-white">
            <p className="text-xl font-bold text-white">4</p>
          </div>
          <p className="text-xl font-bold">{t("reCustomQuotationContent.parcelTitle")}</p>
        </div>
        <div className="flex w-full flex-col gap-1">
          <div className="flex flex-col items-center justify-center gap-4">
            <h2 className="text-brand700 text-xl font-semibold">
              {t("reCustomQuotationContent.parcelProductsTitle")}
            </h2>
            <div className="flex w-full flex-col items-end gap-2">
              {parcelProducts?.map((field, index) => (
                <div
                  className="flex w-full flex-col items-start gap-2"
                  key={`parcelProduct-${index}`}
                >
                  <p className="font-semibold text-gray-700">
                    {t("reCustomQuotationContent.productTitle", { number: index + 1 })}
                  </p>
                  <div className="flex w-full flex-col items-start gap-2">
                    <ControlledSelectV2
                      name={`parcelProducts.${index}.type`}
                      control={methods.control}
                      options={parcelProductTypeOptions}
                      placeholder={t("reCustomQuotationContent.goodsTypePlaceholder")}
                      label={t("reCustomQuotationContent.goodsTypeLabel")}
                      wrapperClassName="w-full"
                    />
                    {field.type === "BOTTLE" ? (
                      <div className="flex w-full flex-col items-start gap-2 md:flex-row">
                        <div className="flex w-full flex-1 flex-col items-start sm:flex-row sm:gap-2">
                          <ControlledSelectV2
                            options={volumesOptions.map((volume) => ({
                              label: volume.label,
                              value: volume.value.toString(),
                            }))}
                            placeholder={t("reCustomQuotationContent.volumePlaceholder")}
                            label={t("reCustomQuotationContent.volumeLabel")}
                            control={methods.control}
                            name={`parcelProducts.${index}.volume`}
                            wrapperClassName="w-full"
                          />
                          <div className="flex w-full flex-row items-start gap-2">
                            <ControlledUnifiedInput
                              label={t("reCustomQuotationContent.bottleQuantityLabel")}
                              control={methods.control}
                              name={`parcelProducts.${index}.bottleQuantity`}
                              className="w-full"
                              inputType="integer"
                              wrapperClassName="w-full"
                            />
                            {parcelProducts?.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeParcelProduct(index)}
                                className="mt-[15px] flex items-start justify-center hover:bg-gray-100 focus:outline-hidden"
                              >
                                <Trash className="cursor-pointer text-red-600" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex w-full flex-col items-start gap-2 md:flex-row">
                        <div className="flex w-full flex-1 flex-row items-start gap-2">
                          <ControlledCustomTextarea
                            label={t("reCustomQuotationContent.descriptionLabel")}
                            control={methods.control}
                            name={`parcelProducts.${index}.description`}
                            className="w-full"
                            wrapperClassName="w-full"
                          />
                          {parcelProducts?.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeParcelProduct(index)}
                              className="mt-[15px] flex items-start justify-center hover:bg-gray-100 focus:outline-hidden"
                            >
                              <Trash className="cursor-pointer text-red-600" />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addParcelProduct()}
                className="bg-brand50 text-brand700 hover:border-brand600 h-fit w-fit rounded-lg px-4 py-3 text-base font-semibold focus:outline-hidden"
              >
                {t("reCustomQuotationContent.addProduct")}
              </button>
            </div>
            <h2 className="text-brand700 text-xl font-semibold">
              {t("reCustomQuotationContent.parcelPackagesTitle")}
            </h2>
            <div className="flex w-full flex-col items-end gap-2">
              {parcelPackages?.map((_field, index) => (
                <div
                  className="flex w-full flex-col items-start gap-2"
                  key={`parcelPackage-${index}`}
                >
                  <p className="font-semibold text-gray-700">
                    {t("reCustomQuotationContent.parcelTitleNumbered", { number: index + 1 })}
                  </p>
                  <div className="hidden w-full flex-col items-start gap-2 md:flex">
                    <div className="flex w-full flex-row items-start gap-2">
                      <ControlledUnifiedInput
                        label={t("reCustomQuotationContent.lengthLabel")}
                        control={methods.control}
                        name={`parcelPackages.${index}.length`}
                        inputType="integer"
                        className="w-full"
                        rightElement={<p className="text-xs text-gray-600">cm</p>}
                        wrapperClassName="w-full"
                      />
                      <ControlledUnifiedInput
                        label={t("reCustomQuotationContent.widthLabel")}
                        control={methods.control}
                        name={`parcelPackages.${index}.width`}
                        inputType="integer"
                        className="w-full"
                        rightElement={<p className="text-xs text-gray-600">cm</p>}
                        wrapperClassName="w-full"
                      />
                      <ControlledUnifiedInput
                        label={t("reCustomQuotationContent.heightLabel")}
                        control={methods.control}
                        name={`parcelPackages.${index}.height`}
                        inputType="integer"
                        className="w-full"
                        rightElement={<p className="text-xs text-gray-600">cm</p>}
                        wrapperClassName="w-full"
                      />
                    </div>
                    <div className="flex w-full flex-row items-start gap-2">
                      <ControlledUnifiedInput
                        label={t("reCustomQuotationContent.weightLabel")}
                        control={methods.control}
                        name={`parcelPackages.${index}.weight`}
                        inputType="integer"
                        className="w-full"
                        rightElement={<p className="text-xs text-gray-600">kg</p>}
                        wrapperClassName="w-full"
                      />
                      <ControlledUnifiedInput
                        label={t("reCustomQuotationContent.valueLabel")}
                        control={methods.control}
                        name={`parcelPackages.${index}.value`}
                        className="w-full"
                        rightElement={<p>€</p>}
                        tooltip={t("reCustomQuotationContent.parcelValueTooltip")}
                        inputType="integer"
                        wrapperClassName="w-full"
                      />
                      {parcelPackages?.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeParcelPackage(index)}
                          className="mt-[15px] flex items-start justify-center hover:bg-gray-100 focus:outline-hidden"
                        >
                          <Trash className="cursor-pointer text-red-600" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-start gap-2 md:hidden md:flex-row">
                    <div className="flex flex-row items-start gap-2">
                      <ControlledUnifiedInput
                        label={t("reCustomQuotationContent.lengthLabel")}
                        control={methods.control}
                        name={`parcelPackages.${index}.length`}
                        inputType="integer"
                        className="w-full"
                        rightElement={<p className="text-xs text-gray-600">cm</p>}
                      />
                      <ControlledUnifiedInput
                        label={t("reCustomQuotationContent.widthLabel")}
                        control={methods.control}
                        name={`parcelPackages.${index}.width`}
                        inputType="integer"
                        className="w-full"
                        rightElement={<p className="text-xs text-gray-600">cm</p>}
                      />
                    </div>
                    <div className="flex flex-row items-start gap-2">
                      <ControlledUnifiedInput
                        label={t("reCustomQuotationContent.heightLabel")}
                        control={methods.control}
                        name={`parcelPackages.${index}.height`}
                        inputType="integer"
                        className="w-full"
                        rightElement={<p className="text-xs text-gray-600">cm</p>}
                      />
                      <ControlledUnifiedInput
                        label={t("reCustomQuotationContent.weightLabel")}
                        control={methods.control}
                        name={`parcelPackages.${index}.weight`}
                        inputType="integer"
                        className="w-full"
                        rightElement={<p className="text-xs text-gray-600">kg</p>}
                      />
                    </div>
                    <div className="flex w-full flex-row items-start gap-2">
                      <ControlledUnifiedInput
                        label={t("reCustomQuotationContent.valueLabel")}
                        control={methods.control}
                        name={`parcelPackages.${index}.value`}
                        className="w-full"
                        rightElement={<p>€</p>}
                        tooltip={t("reCustomQuotationContent.parcelValueTooltip")}
                      />
                      {parcelPackages?.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeParcelPackage(index)}
                          className="mt-[15px] flex items-start justify-center hover:bg-gray-100 focus:outline-hidden"
                        >
                          <Trash className="cursor-pointer text-red-600" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addParcelPackage()}
                className="bg-brand50 text-brand700 hover:border-brand600 h-fit w-fit rounded-lg px-4 py-3 text-base font-semibold focus:outline-hidden"
              >
                {t("reCustomQuotationContent.addParcel")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
};
